/**
 * The dot, and the hook behind it.
 *
 * The part that carries meaning — which colour, and whether do-not-disturb gets
 * its bar — is `presenceDot()` in `@shared/presence`, so the apps cannot
 * disagree about it. What is here is the two elements around it and the
 * subscription: Scope draws this on a 30px initials chip in the top nav and on
 * the 10px swatches in the picker under it, and a shared component would have
 * arrived with a prop for each.
 *
 * The socket itself is `@shared/presence-client`, started once by AppProvider.
 */

import { useSyncExternalStore } from 'react';
import type React from 'react';

import { getPresence, getPresenceOverride, subscribePresence } from '@shared/presence-client';
import { presenceDot, type Presence } from '@shared/presence';

/**
 * What to draw, and what was chosen.
 *
 * Both are plain strings, so the snapshot is referentially stable and
 * `useSyncExternalStore` has nothing to loop on.
 *
 * The two are not the same question. Somebody who chose "available" and then
 * went to lunch is drawn as away, and a tick against "Away" in the picker would
 * be telling them they chose something they did not.
 */
export function usePresence(): { presence: Presence; override: Presence | null } {
  const presence = useSyncExternalStore(subscribePresence, getPresence, () => 'unknown' as const);
  const override = useSyncExternalStore(subscribePresence, getPresenceOverride, () => null);
  return { presence, override };
}

/**
 * A dot in the corner of whatever it is given.
 *
 * The caller supplies the positioned parent — this is `position: absolute` and
 * needs something to be absolute inside.
 *
 * `ring` is the **chip's** background rather than the page's, because the dot
 * cuts itself out of it: the nav chip is `--accent-soft` over the glass bar and
 * the picker's swatches are on `--surface-card`, and a dot ringed in the wrong
 * one has a visible seam.
 *
 * Renders nothing for `unknown`, which is what a page that has not heard yet
 * shows.
 */
export function PresenceDot({
  presence,
  size = 9,
  ring
}: {
  presence: Presence;
  size?: number;
  ring: string;
}) {
  const style = presenceDot(presence, size, ring);
  if (!style) return null;

  return (
    <span aria-hidden="true" style={style.dot as React.CSSProperties}>
      {style.bar && <span style={style.bar as React.CSSProperties} />}
    </span>
  );
}
